
// client/src/components/messages/MessageThreadModal.jsx
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useMessagesUI } from '../../context/MessagesContext';
import {
  getThreads,
  getThread,
  sendMessage,
  deleteMessage,
  deleteThread,
  markThreadAsRead,
  searchUsers,
  createThread
} from '../../services/messagesApi';
import { getSessionLocal } from '../../services/sessionLocal';
import ReadReceipt, { receiptLabel } from './ReadReceipt';
import {
  hueForUser,
  getReceipt,
  buildRenderItems,
  formatDayLabel,
  formatClock,
  initials,
} from './messageUi';
import './MessageThreadModal.css';

// How often an open thread re-fetches to pick up new messages and read
// receipts. The client has no live socket connection, so this is what keeps
// the ✓ / ✓✓ ticks current. Paused while the tab is hidden.
const THREAD_POLL_MS = 8000;

const truncateName = (name) =>
  name && name.length > 15 ? `${name.slice(0, 15)}…` : (name || '');

export default function MessageThreadModal({ tenantId: propTenantId }) {
  const { isOpen, closeMessages, activeThreadId } = useMessagesUI();
  
  // Get session data
  const session = useMemo(() => getSessionLocal(), []);
  const tenantId = propTenantId || session.tenantId;
  const myUserId = useMemo(() => {
    const me = session?.userId || session?.userid;
    try {
      const stored = localStorage.getItem('me');
      if (stored) {
        const parsed = JSON.parse(stored);
        return Number(parsed?.id || parsed?.userid || parsed?.userId || me || 0);
      }
    } catch {}
    return Number(me || 0);
  }, [session]);
  
  // Thread list state
  const [threads, setThreads] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [threadsError, setThreadsError] = useState(null);
  
  // Active thread state
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState(null);
  // [{ userId, lastReadMessageId, lastReadAt }] — drives read receipts
  const [readState, setReadState] = useState([]);
  
  // Compose state
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  
  // New thread state
  const [showNewThread, setShowNewThread] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [creatingThread, setCreatingThread] = useState(false);

  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const selectedThreadIdRef = useRef(null);
  selectedThreadIdRef.current = selectedThread?.id ?? null;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
      
      if (isNearBottom || messages.length === 1) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [messages]);

  // Load threads when modal opens
  useEffect(() => {
    if (!isOpen) return;
    loadThreads();
  }, [isOpen]);

  // When activeThreadId is set (e.g. from a push notification deep link),
  // open the thread once threads have been loaded.
  useEffect(() => {
    if (!isOpen || !activeThreadId || loadingThreads) return;
    // openThread() itself updates `threads` (marking the thread read), which
    // would otherwise re-trigger this effect and re-open the same thread
    // forever — only act the first time this deep link's target isn't
    // already the open thread.
    if (selectedThread?.id === activeThreadId) return;
    const thread = threads.find(t => t.id === activeThreadId);
    if (thread) {
      openThread(thread);
    } else if (threads.length > 0) {
      // Thread not in list (deleted or different tenant) — open by id anyway
      openThread({ id: activeThreadId });
    }
  }, [isOpen, activeThreadId, loadingThreads, threads, selectedThread]);

  // Load threads
  const loadThreads = async ({ skipCleanup = false } = {}) => {
    if (!tenantId) {
      setThreadsError('No tenant ID available');
      return;
    }

    setLoadingThreads(true);
    setThreadsError(null);

    try {
      const result = await getThreads({ tenantId });
      const threadList = result?.threads || result || [];
      const list = Array.isArray(threadList) ? threadList : [];

      if (skipCleanup) {
        setThreads(list);
      } else {
        // Auto-cleanup: drop and delete threads that have no messages.
        // skipCleanup is passed from flows where an in-progress thread may
        // legitimately be empty (just-created thread, last message deleted).
        const hasContent = (t) => !!(t?.lastMessage || t?.last_message);
        const kept = list.filter(hasContent);
        const empty = list.filter((t) => !hasContent(t));
        setThreads(kept);

        empty.forEach((t) => {
          if (!t?.id) return;
          deleteThread(t.id, { tenantId }).catch((err) =>
            console.warn('Auto-cleanup failed for empty thread', t.id, err)
          );
        });
      }
    } catch (error) {
      console.error('Failed to load threads:', error);
      setThreadsError(error.message || 'Failed to load conversations');
      setThreads([]);
    } finally {
      setLoadingThreads(false);
    }
  };

  // Open a thread
  const openThread = async (threadArg) => {
    // Prefer the full thread object from the loaded list (has participants, title, etc.)
    const thread = threads.find(t => t.id === threadArg.id) || threadArg;
    setSelectedThread(thread);
    setShowNewThread(false);
    setLoadingMessages(true);
    setMessagesError(null);
    setMessages([]);
    setReadState([]);
    
    try {
      const result = await getThread(thread.id, { tenantId });
      const threadData = result?.thread || result;
      const messageList = threadData?.messages || [];
      setMessages(Array.isArray(messageList) ? messageList : []);
      setReadState(Array.isArray(threadData?.readState) ? threadData.readState : []);

      // Update selectedThread with full data (participants, title) from the API
      if (threadData?.participants) {
        setSelectedThread(prev => ({ ...prev, ...threadData, messages: undefined }));
      }
      
      // Mark as read
      try {
        await markThreadAsRead(thread.id, { tenantId });
        
        // Update thread list to reflect read status
        setThreads(prev => 
          prev.map(t => t.id === thread.id ? { ...t, unreadCount: 0, unread_count: 0 } : t)
        );
      } catch (readError) {
        console.warn('Failed to mark as read:', readError);
      }
    } catch (error) {
      console.error('Failed to load thread:', error);
      setMessagesError(error.message || 'Failed to load messages');
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Keep the open thread fresh: new incoming messages + other participants'
  // read watermarks. Only marks the thread read while the tab is visible, so
  // a backgrounded app never sends a false "read".
  useEffect(() => {
    const threadId = selectedThread?.id;
    if (!isOpen || !threadId || loadingMessages) return undefined;

    const refresh = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const result = await getThread(threadId, { tenantId });
        if (selectedThreadIdRef.current !== threadId) return;
        const threadData = result?.thread || result;
        const serverMessages = Array.isArray(threadData?.messages) ? threadData.messages : [];
        const known = new Set(messagesRef.current.map((m) => m.id));
        const hasNewIncoming = serverMessages.some(
          (m) => !known.has(m.id) && Number(m.senderId || m.sender_id) !== myUserId
        );

        // Keep local bubbles that the server doesn't know about yet
        setMessages((prev) => [...serverMessages, ...prev.filter((m) => m.isOptimistic || m.failed)]);
        if (Array.isArray(threadData?.readState)) setReadState(threadData.readState);

        if (hasNewIncoming) {
          await markThreadAsRead(threadId, { tenantId });
          setThreads((prev) =>
            prev.map((t) => (t.id === threadId ? { ...t, unreadCount: 0, unread_count: 0 } : t))
          );
        }
      } catch (err) {
        console.warn('Thread refresh failed:', err);
      }
    };

    const timer = setInterval(refresh, THREAD_POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('message:read', refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('message:read', refresh);
    };
  }, [isOpen, selectedThread?.id, loadingMessages, tenantId, myUserId]);

  // Post a message text, showing it immediately and swapping in the server
  // copy when it lands. On failure the bubble stays with a retry button.
  const postMessage = async (text, existingId) => {
    const optimisticId = existingId || `temp-${Date.now()}`;
    const optimisticMessage = {
      id: optimisticId,
      text,
      senderId: myUserId,
      sender_id: myUserId,
      createdAt: new Date().toISOString(),
      created_at: new Date().toISOString(),
      isOptimistic: true
    };
    setMessages(prev => existingId
      ? prev.map(m => m.id === existingId ? optimisticMessage : m)
      : [...prev, optimisticMessage]);

    try {
      const result = await sendMessage(selectedThread.id, text, { tenantId });
      const newMessage = result?.message || result;
      if (!newMessage) throw new Error('Empty response');

      // A refresh may already have pulled the real message in
      setMessages(prev => prev.some(m => m.id === newMessage.id)
        ? prev.filter(m => m.id !== optimisticId)
        : prev.map(m => m.id === optimisticId ? newMessage : m));

      setThreads(prev =>
        prev.map(t => t.id === selectedThread.id ? {
          ...t,
          lastMessage: text,
          last_message: text,
          lastAt: newMessage.createdAt || newMessage.created_at,
          last_at: newMessage.createdAt || newMessage.created_at
        } : t)
      );
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev =>
        prev.map(m => m.id === optimisticId ? { ...m, isOptimistic: false, failed: true } : m)
      );
    }
  };

  const handleRetry = (msg) => {
    if (!msg?.failed || !selectedThread?.id) return;
    postMessage(msg.text, msg.id);
  };

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || !selectedThread?.id || sending) return;
    
    setSending(true);
    const text = messageText.trim();
    setMessageText('');
    try {
      await postMessage(text);
    } finally {
      setSending(false);
    }
  };

  // Delete message
  const handleDeleteMessage = async (msg) => {
    const msgId = msg?.id;
    if (!msgId || !selectedThread?.id) return;
    if (!window.confirm('Delete this message?')) return;

    // Optimistically remove
    setMessages(prev => prev.filter(m => m.id !== msgId));

    try {
      await deleteMessage(selectedThread.id, msgId, { tenantId });
      // Refresh thread list to update last_message preview.
      // Skip cleanup: the user is actively viewing this thread, even if it
      // just became empty — don't auto-delete it out from under them.
      loadThreads({ skipCleanup: true });
    } catch (error) {
      console.error('Failed to delete message:', error);
      // Re-fetch to restore
      try {
        const result = await getThread(selectedThread.id, { tenantId });
        setMessages(result?.thread?.messages || []);
      } catch {}
    }
  };

  // Delete thread
  const handleDeleteThread = async (thread, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (!thread?.id) return;
    if (!window.confirm('Delete this entire conversation? This cannot be undone.')) return;

    const threadId = thread.id;

    // If we're viewing this thread, go back to list
    if (selectedThread?.id === threadId) {
      backToList();
    }

    // Optimistically remove from list
    setThreads(prev => prev.filter(t => t.id !== threadId));

    try {
      await deleteThread(threadId, { tenantId });
    } catch (error) {
      console.error('Failed to delete thread:', error);
      // Re-fetch to restore
      loadThreads();
    }
  };

  // Search users with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (userSearchQuery && userSearchQuery.trim().length >= 2) {
        handleUserSearch(userSearchQuery);
      } else {
        setUserSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [userSearchQuery]);

  const handleUserSearch = async (query) => {
    if (!query || query.trim().length < 2) {
      setUserSearchResults([]);
      return;
    }
    
    setSearchingUsers(true);
    try {
      const result = await searchUsers(query, { tenantId });
      const users = result?.users || [];
      // Filter out current user
      const filtered = users.filter(u => {
        const uid = Number(u?.id || u?.userid || u?.userId || 0);
        return uid !== myUserId && uid > 0;
      });
      setUserSearchResults(filtered);
    } catch (error) {
      console.error('User search failed:', error);
      setUserSearchResults([]);
    } finally {
      setSearchingUsers(false);
    }
  };

  // Toggle user selection
  const toggleUserSelection = (user) => {
    const uid = Number(user?.id || user?.userid || user?.userId || 0);
    setSelectedUsers(prev => {
      const exists = prev.find(u => {
        const existingId = Number(u?.id || u?.userid || u?.userId || 0);
        return existingId === uid;
      });
      if (exists) {
        return prev.filter(u => {
          const existingId = Number(u?.id || u?.userid || u?.userId || 0);
          return existingId !== uid;
        });
      } else {
        return [...prev, user];
      }
    });
  };

  // Create new thread
  const handleCreateThread = async () => {
    if (selectedUsers.length === 0 || creatingThread) return;
    
    setCreatingThread(true);
    try {
      const participantIds = selectedUsers.map(u => 
        Number(u?.id || u?.userid || u?.userId || 0)
      ).filter(id => id > 0);
      
      const result = await createThread(participantIds, { tenantId });
      const newThread = result?.thread || result;

      if (newThread?.id) {
        // Reload full thread list so we get complete participant data.
        // Skip cleanup: the new thread is empty by design until the first
        // message is sent.
        await loadThreads({ skipCleanup: true });

        // Open the new/existing thread
        setSelectedThread({ id: newThread.id });
        setMessages([]);
        setShowNewThread(false);
        setSelectedUsers([]);
        setUserSearchQuery('');
        setUserSearchResults([]);

        // Load the thread's messages
        openThread({ id: newThread.id });
      }
    } catch (error) {
      console.error('Failed to create thread:', error);
      alert('Failed to create conversation: ' + (error.message || 'Unknown error'));
    } finally {
      setCreatingThread(false);
    }
  };

  // Back to thread list
  const backToList = () => {
    setSelectedThread(null);
    setMessages([]);
    setReadState([]);
    setShowNewThread(false);
    setSelectedUsers([]);
    setUserSearchQuery('');
    setUserSearchResults([]);
  };

  // Close modal
  const handleClose = () => {
    backToList();
    closeMessages();
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Format thread display name.
  // Task/system threads use their title; regular conversations show the other participant(s).
  const getThreadDisplayName = (thread) => {
    if (thread?.title) return thread.title;

    const participants = thread?.participants || [];
    const otherParticipants = participants.filter(p => {
      const pid = Number(p?.id || p?.userid || p?.userId || 0);
      return pid !== myUserId && pid > 0;
    });

    if (otherParticipants.length > 0) {
      return otherParticipants
        .map(p => truncateName(p?.displayName || p?.username || p?.email || 'Unknown'))
        .join(', ');
    }

    // Fallback: show all participant names if filtering removed everyone
    if (participants.length > 0) {
      return participants
        .map(p => truncateName(p?.displayName || p?.username || p?.email || 'Unknown'))
        .join(', ');
    }

    return 'Conversation';
  };

  // Format message timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    // Less than 1 minute
    if (diff < 60000) return 'Just now';
    
    // Less than 1 hour
    if (diff < 3600000) {
      const minutes = Math.floor(diff / 60000);
      return `${minutes}m ago`;
    }
    
    // Less than 24 hours
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return `${hours}h ago`;
    }
    
    // Today
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    
    // This week
    if (diff < 604800000) {
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    
    // Older
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // ---- Message rendering helpers -------------------------------------
  const participants = selectedThread?.participants || [];
  const isGroupThread = participants.length > 2;

  const senderNameFor = (msg) => {
    const id = Number(msg?.senderId || msg?.sender_id || 0);
    const p = participants.find(x => Number(x?.id || x?.userid || x?.userId || 0) === id);
    return p?.displayName || msg?.senderDisplayName || msg?.senderUsername || p?.username || 'Unknown';
  };

  const receiptFor = (msg) => getReceipt(msg, { participants, readState, myUserId });

  const renderItems = useMemo(() => buildRenderItems(messages), [messages]);

  // 1:1 threads show a single "Seen" under my newest read message (iMessage
  // style) rather than repeating it under every bubble.
  let lastSeenOwnId = null;
  if (!isGroupThread) {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (Number(m?.senderId || m?.sender_id || 0) !== myUserId) continue;
      if (receiptFor(m).status === 'read') { lastSeenOwnId = m.id; break; }
    }
  }

  if (!isOpen) return null;

  return (
    <div className="messages-modal-backdrop" onClick={handleBackdropClick}>
      <div className="messages-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="messages-modal__header">
          {(selectedThread || showNewThread) && (
            <button
              className="messages-modal__back-btn btn btn-sm btn-link text-white p-0 no-underline"
              onClick={backToList}
            >
              ←
            </button>
          )}
          <h5 className="messages-modal__title m-0">
            {selectedThread
              ? getThreadDisplayName(selectedThread)
              : showNewThread
              ? 'New Message'
              : 'Messages'}
          </h5>
          <button
            className="messages-modal__close-btn btn btn-sm btn-outline-light"
            onClick={handleClose}
          >
            Close
          </button>
        </div>

        {/* Body */}
        <div className="messages-modal__body">
          {/* Sidebar - Thread List */}
          <aside 
            className={`messages-modal__sidebar ${selectedThread ? 'thread-selected' : ''}`}
            style={{ display: selectedThread ? 'none' : 'block' }}
          >
            {!showNewThread && (
              <>
                <div className="p-3">
                  <button 
                    className="btn btn-primary w-full"
                    onClick={() => setShowNewThread(true)}
                  >
                    + New Message
                  </button>
                </div>

                {loadingThreads && (
                  <div className="text-center py-4">
                    <div className="spinner-border text-gray-200" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}

                {threadsError && (
                  <div className="alert alert-danger mx-3" role="alert">
                    {threadsError}
                  </div>
                )}

                {!loadingThreads && !threadsError && threads.length === 0 && (
                  <div className="text-center text-gray-400 py-6">
                    <p className="mb-2">No messages yet</p>
                    <p className="small">Click "New Message" to start a conversation</p>
                  </div>
                )}

                {!loadingThreads && threads.length > 0 && (
                  <div className="list-group list-group-flush">
                    {threads.map(thread => {
                      const displayName = getThreadDisplayName(thread);
                      const lastMessage = thread?.lastMessage || thread?.last_message || '';
                      const unreadCount = Number(thread?.unreadCount || thread?.unread_count || 0);
                      const lastAt = thread?.lastAt || thread?.last_at || thread?.updated_at;

                      return (
                        <div key={thread.id} className="thread-item-wrapper relative">
                          <button
                            className="thread-item list-group-item list-group-item-action"
                            onClick={() => openThread(thread)}
                          >
                            <div className="flex justify-between items-start mb-1">
                              <strong className="truncate flex-grow">{displayName}</strong>
                              {lastAt && (
                                <small className="text-gray-400 ml-2" style={{ whiteSpace: 'nowrap' }}>
                                  {formatTimestamp(lastAt)}
                                </small>
                              )}
                            </div>
                            <div className="flex justify-between items-center">
                              <div className="text-gray-400 small truncate flex-grow">
                                {lastMessage || 'No messages yet'}
                              </div>
                              {unreadCount > 0 && (
                                <span className="badge bg-cobalt rounded-full ml-2">
                                  {unreadCount}
                                </span>
                              )}
                            </div>
                          </button>
                          <button
                            className="thread-delete-btn"
                            onClick={(e) => handleDeleteThread(thread, e)}
                            title="Delete conversation"
                          >
                            &times;
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* New Message View */}
            {showNewThread && (
              <div className="p-3">
                <div className="mb-3">
                  <label className="form-label text-white">To:</label>
                  <input
                    type="text"
                    className="form-control bg-dark-bg text-white border-gray-500"
                    placeholder="Search users..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    autoFocus
                  />
                </div>

                {selectedUsers.length > 0 && (
                  <div className="mb-3">
                    <div className="flex flex-wrap gap-2 mb-2">
                      {selectedUsers.map(user => {
                        const uid = Number(user?.id || user?.userid || user?.userId || 0);
                        const name = truncateName(user?.displayName || user?.username || user?.email || 'Unknown');
                        return (
                          <span key={uid} className="badge bg-cobalt flex items-center gap-1">
                            {name}
                            <button
                              type="button"
                              className="btn-close btn-close-white"
                              style={{ fontSize: '0.6rem' }}
                              onClick={() => toggleUserSelection(user)}
                            />
                          </span>
                        );
                      })}
                    </div>
                    <button
                      className="btn btn-success w-full"
                      onClick={handleCreateThread}
                      disabled={creatingThread}
                    >
                      {creatingThread ? 'Creating...' : 'Start Conversation'}
                    </button>
                  </div>
                )}

                {searchingUsers && (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm text-gray-200" role="status">
                      <span className="visually-hidden">Searching...</span>
                    </div>
                  </div>
                )}

                {!searchingUsers && userSearchResults.length > 0 && (
                  <div className="list-group">
                    {userSearchResults.map(user => {
                      const uid = Number(user?.id || user?.userid || user?.userId || 0);
                      const name = truncateName(user?.displayName || user?.username || 'Unknown');
                      const email = user?.email || '';
                      const isSelected = selectedUsers.some(u => {
                        const selectedId = Number(u?.id || u?.userid || u?.userId || 0);
                        return selectedId === uid;
                      });

                      return (
                        <button
                          key={uid}
                          className={`search-result-item list-group-item list-group-item-action bg-dark-bg text-white ${isSelected ? 'active' : ''}`}
                          onClick={() => toggleUserSelection(user)}
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={isSelected}
                              readOnly
                            />
                            <div className="flex-grow">
                              <div className="font-semibold">{name}</div>
                              {email && <div className="small text-gray-400">{email}</div>}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {!searchingUsers && userSearchQuery.length >= 2 && userSearchResults.length === 0 && (
                  <div className="text-center text-gray-400 py-3">
                    No users found matching "{userSearchQuery}"
                  </div>
                )}
              </div>
            )}
          </aside>

          {/* Main Content - Message Thread */}
          <main 
            className={`messages-modal__content ${selectedThread ? 'thread-selected' : ''}`}
            style={{ display: selectedThread ? 'flex' : 'none' }}
          >
            {selectedThread && (
              <>
                {loadingMessages && (
                  <div className="flex-grow flex items-center justify-center">
                    <div className="spinner-border text-gray-200" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}

                {messagesError && (
                  <div className="alert alert-danger m-3" role="alert">
                    {messagesError}
                  </div>
                )}

                {!loadingMessages && !messagesError && (
                  <>
                    {/* Messages Area */}
                    <div
                      ref={scrollContainerRef}
                      className="msg-list flex-grow p-3"
                      style={{ overflowY: 'auto', overflowX: 'hidden' }}
                      role="log"
                      aria-live="polite"
                      aria-relevant="additions"
                      aria-label="Messages"
                      tabIndex={0}
                    >
                      {messages.length === 0 && (
                        <div className="text-center text-gray-400 py-6">
                          <p>No messages yet</p>
                          <p className="small">Start the conversation below</p>
                        </div>
                      )}

                      {renderItems.map((item) => {
                        if (item.type === 'day') {
                          return (
                            <div key={item.key} className="msg-day" role="separator">
                              <span>{formatDayLabel(item.date)}</span>
                            </div>
                          );
                        }

                        const msg = item.message;
                        const { position } = item;
                        const senderId = Number(msg?.senderId || msg?.sender_id || 0);
                        const isMine = senderId === myUserId;
                        const text = msg?.text || msg?.body || msg?.content || '';
                        const timestamp = msg?.createdAt || msg?.created_at || msg?.timestamp;
                        const senderName = isMine ? 'You' : senderNameFor(msg);
                        const receipt = isMine ? receiptFor(msg) : null;
                        const showName = isGroupThread && !isMine && (position === 'first' || position === 'single');
                        const showAvatar = isGroupThread && !isMine && (position === 'last' || position === 'single');
                        const clock = timestamp ? formatClock(timestamp) : '';
                        const a11yLabel = [
                          `${senderName}, ${clock}: ${text}`,
                          receipt ? receiptLabel(receipt, isGroupThread) : null,
                        ].filter(Boolean).join('. ');

                        return (
                          <React.Fragment key={item.key}>
                            <div
                              className={`msg-row ${isMine ? 'msg-row--own' : 'msg-row--other'} msg-row--${position}`}
                              style={{ '--user-hue': hueForUser(senderId) }}
                            >
                              {isGroupThread && !isMine && (
                                <div className="msg-avatar" aria-hidden="true">
                                  {showAvatar ? initials(senderNameFor(msg)) : null}
                                </div>
                              )}

                              <div className="msg-stack">
                                {showName && (
                                  <div className="msg-sender" aria-hidden="true">{senderNameFor(msg)}</div>
                                )}

                                <div
                                  className={`message-bubble msg-bubble msg-bubble--${position}`}
                                  role="group"
                                  aria-label={a11yLabel}
                                >
                                  {isMine && !msg.isOptimistic && !msg.failed && (
                                    <button
                                      className="message-delete-btn"
                                      onClick={() => handleDeleteMessage(msg)}
                                      title="Delete message"
                                      aria-label="Delete message"
                                    >
                                      &times;
                                    </button>
                                  )}
                                  <span className="msg-text" aria-hidden="true">{text}</span>
                                  {timestamp && (
                                    <span className="msg-meta" aria-hidden="true">
                                      <time
                                        dateTime={new Date(timestamp).toISOString()}
                                        title={new Date(timestamp).toLocaleString()}
                                      >
                                        {clock}
                                      </time>
                                      {receipt && <ReadReceipt receipt={receipt} isGroup={isGroupThread} />}
                                    </span>
                                  )}
                                </div>

                                {msg.failed && (
                                  <button type="button" className="msg-retry" onClick={() => handleRetry(msg)}>
                                    Not sent · Tap to retry
                                  </button>
                                )}
                              </div>
                            </div>

                            {msg.id === lastSeenOwnId && (
                              <div className="msg-seen" aria-hidden="true">Seen</div>
                            )}
                          </React.Fragment>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Compose Area */}
                    <div className="messages-compose-area">
                      <form onSubmit={handleSendMessage}>
                        <textarea
                          className="messages-compose-input"
                          placeholder="Type a message..."
                          value={messageText}
                          onChange={(e) => setMessageText(e.target.value)}
                          disabled={sending}
                          rows={2}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendMessage(e);
                            }
                          }}
                        />
                        <button
                          type="submit"
                          className="messages-compose-send"
                          disabled={!messageText.trim() || sending}
                        >
                          {sending ? 'Sending...' : 'Send'}
                        </button>
                      </form>
                    </div>
                  </>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}